import { useCallback } from 'react'

import { UseQueryResult, useQueries } from '@tanstack/react-query'

import { FILE_STALE_TIME } from '@/constants/file'

import { userFilesQueryKeys } from '@/hooks/files/types'
import { filesQueryKeys } from '@/hooks/files/types'
import { useSetFileCache } from '@/hooks/files/useFile'
import { fetchOpportunityFiles } from '@/hooks/files/useOpportunityFiles'
import { fetchUserFiles } from '@/hooks/files/useUserFiles'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { fetchOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'
import { pursuitFilesQueryKeys } from '@/hooks/pursuits/types'
import { fetchPursuitFiles } from '@/hooks/pursuits/usePursuitFiles'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { File, PotentiallyUnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { OpportunityType } from '@/types/Opportunity'

const combineAndSort = (
  result: UseQueryResult<PotentiallyUnhydratedFile[], Error>[],
) => {
  let combined: PotentiallyUnhydratedFile[] = []
  for (const query of result) {
    if (query.data) {
      combined.push(...query.data)
    }
  }
  combined.sort((a, b) => {
    let aDate: string = a.last_modified
    let bDate: string = b.last_modified
    return new Date(bDate).getTime() - new Date(aDate).getTime()
  })
  return {
    data: combined,
    isLoading: result.some((query) => query.isLoading),
    isFetching: result.some((query) => query.isFetching),
    isFetched: result.every((query) => query.isFetched),
    isSuccess: result.every((query) => query.isSuccess),
  }
}

interface UseAvailableOpportunityFilesProps {
  orgId: UUID
  opportunityId: UUID
  opportunityType: OpportunityType
  select?: (
    files: PaginatedResponse<PotentiallyUnhydratedFile>,
  ) => PotentiallyUnhydratedFile[]
}

export const useSetFileCacheWrapper = () => {
  const setFileCache = useSetFileCache()
  return useCallback(
    (fetchFn: () => Promise<PaginatedResponse<File>>) => async () => {
      const data = await fetchFn()
      setFileCache(data.data)
      return data
    },
    [setFileCache],
  )
}

export const useOpportunitySourceFiles = ({
  orgId,
  opportunityId,
  opportunityType,
  select,
}: UseAvailableOpportunityFilesProps) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (files: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      if (select) {
        return select(files)
      }
      return files.data
    },
    [select],
  )

  const setFileCacheWrapper = useSetFileCacheWrapper()

  return useQueries({
    queries: [
      {
        queryKey: organizationFilesQueryKeys.organization({ orgId }),
        queryFn: () =>
          fetchOrganizationFiles(orgId, accessToken, { limit: 10000 }),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
      {
        queryKey: filesQueryKeys.opportunity({
          opportunityId,
          opportunityType,
        }),
        queryFn: setFileCacheWrapper(() =>
          fetchOpportunityFiles(opportunityId, opportunityType, accessToken, {
            limit: 10000,
          }),
        ),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
      {
        queryKey: userFilesQueryKeys.organization({ orgId }),
        queryFn: () => fetchUserFiles(accessToken, { limit: 10000 }, orgId),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
    ],
    combine: combineAndSort,
  })
}

interface UseAvailablePursuitFilesProps {
  orgId: UUID
  pursuitId: UUID | ''
  select?: (
    files: PaginatedResponse<PotentiallyUnhydratedFile>,
  ) => PotentiallyUnhydratedFile[]
}

export const usePursuitSourceFiles = ({
  orgId,
  pursuitId,
  select,
}: UseAvailablePursuitFilesProps) => {
  const { accessToken } = useAuthInfoWithThrow()

  const setFileCacheWrapper = useSetFileCacheWrapper()

  const selectFn = useCallback(
    (files: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      if (select) {
        return select(files)
      }
      return files.data
    },
    [select],
  )

  return useQueries({
    queries: [
      {
        queryKey: pursuitFilesQueryKeys.pursuit({ pursuitId }),
        queryFn: setFileCacheWrapper(() =>
          fetchPursuitFiles(pursuitId, accessToken, { limit: 10000 }),
        ),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        enabled: !!pursuitId,
        select: selectFn,
      },
      {
        queryKey: organizationFilesQueryKeys.organization({ orgId }),
        queryFn: () =>
          fetchOrganizationFiles(orgId, accessToken, { limit: 10000 }),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
      {
        queryKey: userFilesQueryKeys.organization({ orgId }),
        queryFn: () => fetchUserFiles(accessToken, { limit: 10000 }, orgId),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
    ],
    combine: combineAndSort,
  })
}

interface UseAvailableOrganizationFilesProps {
  orgId: UUID
  select?: (
    files: PaginatedResponse<PotentiallyUnhydratedFile>,
  ) => PotentiallyUnhydratedFile[]
}

export const useOrganizationSourceFiles = ({
  orgId,
  select,
}: UseAvailableOrganizationFilesProps) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (files: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      if (select) {
        return select(files)
      }
      return files.data
    },
    [select],
  )

  return useQueries({
    queries: [
      {
        queryKey: organizationFilesQueryKeys.organization({ orgId }),
        queryFn: () =>
          fetchOrganizationFiles(orgId, accessToken, { limit: 10000 }),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
      {
        queryKey: userFilesQueryKeys.organization({ orgId }),
        queryFn: () => fetchUserFiles(accessToken, { limit: 10000 }, orgId),
        staleTime: FILE_STALE_TIME,
        refetchOnWindowFocus: false,
        select: selectFn,
      },
    ],
    combine: combineAndSort,
  })
}
